import api from 'api/config';

/**
 * Validate contract creation data
 * Defined before contractsService to ensure it's available when needed
 * @param {Object} contractData - Contract data to validate
 * @returns {Object} Validation result with:
 *   - isValid {boolean} - Whether the data is valid
 *   - errors {Object} - Object mapping field names to error messages (empty if valid)
 */
export const validateContractData = (contractData) => {
  const errors = {};

  // Guard against null/undefined input
  if (!contractData) {
    return {
      isValid: false,
      errors: { global: 'Contract data is required' },
    };
  }

  // Title validation - must be a string with 5-200 characters
  const title = contractData.title;
  if (typeof title !== 'string' || title.trim().length < 5) {
    errors.title = 'Title must be at least 5 characters';
  } else if (title.length > 200) {
    errors.title = 'Title must be 200 characters or less';
  }

  // Description validation - must be a string with at least 20 characters
  const description = contractData.description;
  if (typeof description !== 'string' || description.trim().length < 20) {
    errors.description = 'Description must be at least 20 characters';
  }

  // Business ID validation - must be a positive number
  const businessId = contractData.business_id;
  if (!businessId || typeof businessId !== 'number' || businessId <= 0) {
    errors.business_id = 'Business ID is required';
  }

  // Amount validation - must be a positive number
  const amount = contractData.amount_usd;
  if (contractData.payment_type === 'fixed') {
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
      errors.amount_usd =
        'Amount must be greater than 0 for fixed price contracts';
    }
  }

  // Payment type validation - must be 'fixed' or 'hourly'
  const paymentType = contractData.payment_type;
  if (!paymentType || !['fixed', 'hourly'].includes(paymentType)) {
    errors.payment_type = 'Payment type must be either "fixed" or "hourly"';
  }

  // Hourly rate validation - required for hourly contracts, must be positive number
  if (paymentType === 'hourly') {
    const hourlyRate = contractData.hourly_rate;
    if (
      typeof hourlyRate !== 'number' ||
      !Number.isFinite(hourlyRate) ||
      hourlyRate <= 0
    ) {
      errors.hourly_rate =
        'Hourly rate is required for hourly contracts and must be greater than 0';
    }
  }

  // Primary category validation - must be a non-empty string
  const primaryCategory = contractData.primary_category;
  if (
    typeof primaryCategory !== 'string' ||
    primaryCategory.trim().length === 0
  ) {
    errors.primary_category = 'Primary category is required';
  }

  // Expertise level validation - must be a non-empty string
  const expertiseLevel = contractData.expertise_level;
  if (
    typeof expertiseLevel !== 'string' ||
    expertiseLevel.trim().length === 0
  ) {
    errors.expertise_level = 'Expertise level is required';
  }

  // Milestones validation for fixed price
  if (
    contractData.milestones &&
    contractData.milestones.length === 0 &&
    paymentType === 'fixed'
  ) {
    // We won't block submission on this but it's good to have consistent validation logic
    // errors.milestones = 'At least one milestone is recommended for fixed price contracts';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Contracts Service for managing contracts, milestones, submissions, payments, reviews, and disputes
 * Provides methods to interact with the contract marketplace API endpoints
 */
export const contractsService = {
  // ========== CONTRACTS ==========

  /**
   * Get contracts with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.pageSize - Items per page (default: 20)
   * @param {string} params.status - Contract status filter
   * @param {string} params.paymentType - Payment type filter ('fixed' or 'hourly')
   * @param {string} params.category - Primary category filter
   * @param {string} params.expertise - Expertise level filter
   * @param {number} params.minAmount - Minimum amount filter
   * @param {number} params.maxAmount - Maximum amount filter
   * @param {string} params.search - Search term
   * @param {string[]} params.techStack - Tech stack filter array
   * @returns {Promise} Response with paginated contracts data
   */
  getContracts: async (params = {}) => {
    try {
      const queryParams = {
        page: params.page || 1,
        page_size: params.pageSize || 20,
        ...(params.status && { status: params.status }),
        ...(params.paymentType && { payment_type: params.paymentType }),
        ...(params.category && { category: params.category }),
        ...(params.expertise && { expertise: params.expertise }),
        ...(params.minAmount && { min_amount: params.minAmount }),
        ...(params.maxAmount && { max_amount: params.maxAmount }),
        ...(params.search && { search: params.search }),
        ...(params.techStack && { tech_stack: params.techStack }),
        ...(params.startDate && { start_date: params.startDate }),
        ...(params.endDate && { end_date: params.endDate }),
      };

      const response = await api.get('/contracts', { params: queryParams });
      return {
        success: true,
        data: response.data.data,
        pagination: {
          total: response.data.data.total,
          page: response.data.data.page,
          pageSize: response.data.data.page_size,
          totalPages: response.data.data.total_pages,
          hasNext: response.data.data.has_next,
          hasPrev: response.data.data.has_prev,
        },
      };
    } catch (error) {
      console.error('Failed to get contracts:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve contracts',
      };
    }
  },

  /**
   * Get a single contract by public ID
   * @param {string} publicId - Contract public ID
   * @returns {Promise} Response with contract details
   */
  getContract: async (publicId) => {
    try {
      if (!publicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.get(`/contracts/${publicId}`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get contract:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve contract',
      };
    }
  },

  /**
   * Create a new contract
   * @param {Object} contractData - Contract creation data
   * @param {number} contractData.business_id - Business ID
   * @param {string} contractData.title - Contract title
   * @param {string} contractData.description - Contract description
   * @param {string} contractData.payment_type - Payment type ('fixed' or 'hourly')
   * @param {number} contractData.amount_usd - Contract amount in USD
   * @param {number} contractData.hourly_rate - Hourly rate (for hourly contracts)
   * @param {string} contractData.primary_category - Primary category
   * @param {string} contractData.secondary_category - Secondary category
   * @param {string} contractData.expertise_level - Required expertise level
   * @param {string[]} contractData.required_skills - Required skills array
   * @param {string[]} contractData.tech_stack - Tech stack array
   * @param {string[]} contractData.tags - Tags array
   * @param {Object[]} contractData.milestones - Milestones array
   * @returns {Promise} Response with created contract
   */
  createContract: async (contractData) => {
    // Validate contract data before making API call
    const validation = validateContractData(contractData);
    if (!validation.isValid) {
      return {
        success: false,
        error: Object.values(validation.errors).join(', '),
        errors: validation.errors,
      };
    }

    try {
      // Pass through directly - frontend uses snake_case to match backend
      const payload = {
        business_id: contractData.business_id,
        title: contractData.title,
        description: contractData.description,
        payment_type: contractData.payment_type,
        amount_usd: contractData.amount_usd,
        primary_category: contractData.primary_category,
        expertise_level: contractData.expertise_level,
        language: contractData.language || 'en',
        must_sign_nda: contractData.must_sign_nda || false,
        ...(contractData.hourly_rate && {
          hourly_rate: contractData.hourly_rate,
        }),
        ...(contractData.start_date && { start_date: contractData.start_date }),
        ...(contractData.end_date && { end_date: contractData.end_date }),
        ...(contractData.expected_duration_days && {
          expected_duration_days:
            typeof contractData.expected_duration_days === 'string'
              ? parseInt(contractData.expected_duration_days, 10)
              : contractData.expected_duration_days,
        }),
        ...(contractData.secondary_category && {
          secondary_category: contractData.secondary_category,
        }),
        ...(contractData.timezone_overlap_hours && {
          timezone_overlap_hours:
            typeof contractData.timezone_overlap_hours === 'string'
              ? parseInt(contractData.timezone_overlap_hours, 10)
              : contractData.timezone_overlap_hours,
        }),
        ...(contractData.tech_stack && { tech_stack: contractData.tech_stack }),
        ...(contractData.hosting_requirements && {
          hosting_requirements: contractData.hosting_requirements,
        }),
        // additional_requirements must be an object, not a string
        // If it's a non-empty string, wrap it in an object with a 'notes' key
        ...(contractData.additional_requirements &&
          (typeof contractData.additional_requirements === 'string'
            ? {
                additional_requirements: {
                  notes: contractData.additional_requirements,
                },
              }
            : {
                additional_requirements: contractData.additional_requirements,
              })),
        ...(contractData.required_skills && {
          required_skills: contractData.required_skills,
        }),
        ...(contractData.tags && { tags: contractData.tags }),
        ...(contractData.milestones && { milestones: contractData.milestones }),
      };

      const response = await api.post('/contracts', payload);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to create contract:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create contract',
      };
    }
  },

  /**
   * Update an existing contract
   * @param {string} publicId - Contract public ID
   * @param {Object} updates - Fields to update
   * @returns {Promise} Response with updated contract
   */
  updateContract: async (publicId, updates, isUpdatingContract) => {
    try {
      if (!publicId) {
        throw new Error('Contract public ID is required');
      }

      let payload = {
        ...(updates.title && { title: updates.title }),
        ...(updates.description && { description: updates.description }),
        ...(updates.amountUsd && { amount_usd: updates.amountUsd }),
        ...(updates.primaryCategory && {
          primary_category: updates.primaryCategory,
        }),
        ...(updates.expertiseLevel && {
          expertise_level: updates.expertiseLevel,
        }),
        ...(updates.techStack && { tech_stack: updates.techStack }),
      };

      // Calling from the Update Contract After creation
      if (isUpdatingContract) {
        payload = {
          ...updates,
        };
      }

      const response = await api.put(`/contracts/${publicId}`, payload);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to update contract:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update contract',
      };
    }
  },

  /**
   * Delete a contract
   * @param {string} publicId - Contract public ID
   * @returns {Promise} Response with success message
   */
  deleteContract: async (publicId) => {
    try {
      if (!publicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.delete(`/contracts/${publicId}`);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to delete contract:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete contract',
      };
    }
  },

  /**
   * Update contract status
   * @param {string} publicId - Contract public ID
   * @param {string} status - New status
   * @param {string} disputeReason - Reason for dispute (if status is 'disputed')
   * @returns {Promise} Response with success message
   */
  updateContractStatus: async (publicId, status, disputeReason = null) => {
    try {
      if (!publicId) {
        throw new Error('Contract public ID is required');
      }

      const payload = {
        status,
        ...(disputeReason && { dispute_reason: disputeReason }),
      };

      const response = await api.post(`/contracts/${publicId}/status`, payload);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to update contract status:', error);
      return {
        success: false,
        error:
          error.response?.data?.error || 'Failed to update contract status',
      };
    }
  },

  /**
   * Search contracts
   * @param {string} query - Search query
   * @param {number} limit - Result limit (default: 20)
   * @returns {Promise} Response with search results
   */
  searchContracts: async (query, limit = 20) => {
    try {
      if (!query) {
        throw new Error('Search query is required');
      }

      const response = await api.get('/contracts/search', {
        params: { q: query, limit },
      });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to search contracts:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to search contracts',
      };
    }
  },

  // ========== SUBMISSIONS/PROPOSALS ==========

  /**
   * Submit a proposal for a contract
   * @param {string} contractPublicId - Contract public ID
   * @param {Object} proposalData - Proposal data
   * @param {string} proposalData.proposalText - Proposal text
   * @param {number} proposalData.proposedAmountUsd - Proposed amount
   * @param {number} proposalData.proposedTimelineDays - Proposed timeline in days
   * @param {string} proposalData.coverLetter - Cover letter (optional)
   * @param {string[]} proposalData.portfolioLinks - Portfolio links array
   * @returns {Promise} Response with created proposal
   */
  submitProposal: async (contractPublicId, payload) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.post(
        `/contracts/${contractPublicId}/submit`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to submit proposal:', error);
      // Include details field which contains the actual error message from backend
      const errorDetails = error.response?.data?.details || '';
      const errorMessage =
        error.response?.data?.error || 'Failed to submit proposal';
      return {
        success: false,
        error: errorDetails || errorMessage,
      };
    }
  },

  /**
   * Get submissions for a contract
   * @param {string} contractPublicId - Contract public ID
   * @returns {Promise} Response with submissions data
   */
  getContractSubmissions: async (contractPublicId) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.get(
        `/contracts/${contractPublicId}/submissions`
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get submissions:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve submissions',
      };
    }
  },

  /**
   * Get the current user's submission for a contract
   * @param {string} contractPublicId - Contract public ID
   * @returns {Promise} Response with submission data
   */
  getMySubmission: async (contractPublicId) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.get(
        `/contracts/${contractPublicId}/my-proposal`
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error || 'Failed to retrieve my submission',
      };
    }
  },

  /**
   * Accept a proposal
   * @param {number} submissionId - Submission ID
   * @returns {Promise} Response with success message
   */
  acceptProposal: async (submissionId) => {
    try {
      if (!submissionId) {
        throw new Error('Submission ID is required');
      }

      const response = await api.post(
        `/contracts/submissions/${submissionId}/accept`
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to accept proposal:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to accept proposal',
      };
    }
  },

  /**
   * Reject a proposal
   * @param {number} submissionId - Submission ID
   * @param {string} reason - Rejection reason
   * @returns {Promise} Response with success message
   */
  rejectProposal: async (submissionId, reason) => {
    try {
      if (!submissionId) {
        throw new Error('Submission ID is required');
      }

      const response = await api.post(
        `/contracts/submissions/${submissionId}/reject`,
        {
          reason,
        }
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to reject proposal:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reject proposal',
      };
    }
  },

  // ========== MILESTONES ==========

  /**
   * Get milestones for a contract
   * @param {string} contractPublicId - Contract public ID
   * @returns {Promise} Response with milestones data
   */
  getMilestones: async (contractPublicId) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.get(
        `/contracts/${contractPublicId}/milestones`
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get milestones:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve milestones',
      };
    }
  },

  /**
   * Create a new milestone
   * @param {string} contractPublicId - Contract public ID
   * @param {Object} milestoneData - Milestone data
   * @param {string} milestoneData.title - Milestone title
   * @param {string} milestoneData.description - Milestone description
   * @param {number} milestoneData.amountUsd - Milestone amount
   * @param {string} milestoneData.dueDate - Due date
   * @param {number} milestoneData.sortOrder - Sort order
   * @returns {Promise} Response with created milestone
   */
  createMilestone: async (contractPublicId, milestoneData) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const payload = {
        title: milestoneData.title,
        sort_order: milestoneData.sortOrder || 0,
        ...(milestoneData.description && {
          description: milestoneData.description,
        }),
        ...(milestoneData.dueDate && { due_date: milestoneData.dueDate }),
        ...(milestoneData.amountUsd && { amount_usd: milestoneData.amountUsd }),
        ...(milestoneData.releaseCondition && {
          release_condition: milestoneData.releaseCondition,
        }),
      };

      const response = await api.post(
        `/contracts/${contractPublicId}/milestones`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to create milestone:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create milestone',
      };
    }
  },

  /**
   * Update a milestone
   * @param {number} milestoneId - Milestone ID
   * @param {Object} updates - Milestone updates
   * @returns {Promise} Response with updated milestone
   */
  updateMilestone: async (milestoneId, updates) => {
    try {
      if (!milestoneId) {
        throw new Error('Milestone ID is required');
      }

      const payload = {
        ...(updates.title && { title: updates.title }),
        ...(updates.description && { description: updates.description }),
        ...(updates.dueDate && { due_date: updates.dueDate }),
        ...(updates.amountUsd && { amount_usd: updates.amountUsd }),
        ...(updates.releaseCondition && {
          release_condition: updates.releaseCondition,
        }),
        ...(updates.sortOrder !== undefined && {
          sort_order: updates.sortOrder,
        }),
      };

      const response = await api.put(
        `/contracts/milestones/${milestoneId}`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to update milestone:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update milestone',
      };
    }
  },

  /**
   * Bulk update milestones for a contract
   * @param {string} contractPublicId - Contract public ID
   * @param {Object[]} updated - Array of milestone updates
   * @param {number[]} deleted - Array of milestone IDs to delete
   * @returns {Promise} Response with updated milestones
   */
  bulkUpdateMilestones: async (contractPublicId, payload) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.put(
        `/contracts/${contractPublicId}/milestones/update`,
        payload
      );
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      console.error('Failed to bulk update milestones:', error);
      return {
        success: false,
        error:
          error.response?.data?.error || 'Failed to bulk update milestones',
      };
    }
  },

  /**
   * Mark milestone as completed
   * @param {number} milestoneId - Milestone ID
   * @returns {Promise} Response with success message
   */
  completeMilestone: async (milestoneId) => {
    try {
      if (!milestoneId) {
        throw new Error('Milestone ID is required');
      }

      const response = await api.post(
        `/contracts/milestones/${milestoneId}/complete`
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to complete milestone:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to complete milestone',
      };
    }
  },

  /**
   * Approve a milestone
   * @param {number} milestoneId - Milestone ID
   * @returns {Promise} Response with success message
   */
  approveMilestone: async (milestoneId) => {
    try {
      if (!milestoneId) {
        throw new Error('Milestone ID is required');
      }

      const response = await api.post(
        `/contracts/milestones/${milestoneId}/approve`
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to approve milestone:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to approve milestone',
      };
    }
  },

  // ========== PAYMENTS ==========

  /**
   * Get payments for a contract
   * @param {string} contractPublicId - Contract public ID
   * @returns {Promise} Response with payments data
   */
  getPayments: async (contractPublicId) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.get(`/contracts/${contractPublicId}/payments`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get payments:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve payments',
      };
    }
  },

  /**
   * Create escrow payment
   * @param {string} contractPublicId - Contract public ID
   * @param {Object} paymentData - Payment data
   * @param {number} paymentData.amountUsd - Payment amount
   * @param {number} paymentData.milestoneId - Milestone ID (optional)
   * @param {string} paymentData.paymentProcessor - Payment processor
   * @param {string} paymentData.externalPaymentId - External payment ID
   * @returns {Promise} Response with created payment
   */
  createEscrowPayment: async (contractPublicId, paymentData) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const payload = {
        amount_usd: paymentData.amountUsd,
        payment_processor: paymentData.paymentProcessor,
        external_payment_id: paymentData.externalPaymentId,
        ...(paymentData.milestoneId && {
          milestone_id: paymentData.milestoneId,
        }),
      };

      const response = await api.post(
        `/contracts/${contractPublicId}/payments/escrow`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to create escrow payment:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create escrow payment',
      };
    }
  },

  /**
   * Release payment from escrow
   * @param {number} paymentId - Payment ID
   * @returns {Promise} Response with success message
   */
  releasePayment: async (paymentId) => {
    try {
      if (!paymentId) {
        throw new Error('Payment ID is required');
      }

      const response = await api.post(
        `/contracts/payments/${paymentId}/release`
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to release payment:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to release payment',
      };
    }
  },

  // ========== REVIEWS ==========

  /**
   * Create a review for a contract
   * @param {string} contractPublicId - Contract public ID
   * @param {Object} reviewData - Review data
   * @param {number} reviewData.rating - Rating (1-5)
   * @param {string} reviewData.reviewText - Review text (optional)
   * @param {boolean} reviewData.isPublic - Whether review is public
   * @returns {Promise} Response with created review
   */
  createReview: async (contractPublicId, reviewData) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const payload = {
        rating: reviewData.rating,
        is_public:
          reviewData.isPublic !== undefined ? reviewData.isPublic : true,
        ...(reviewData.reviewText && { review_text: reviewData.reviewText }),
      };

      const response = await api.post(
        `/contracts/${contractPublicId}/review`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to create review:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create review',
      };
    }
  },

  /**
   * Get reviews for a contract
   * @param {string} contractPublicId - Contract public ID
   * @returns {Promise} Response with reviews data
   */
  getContractReviews: async (contractPublicId) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.get(`/contracts/${contractPublicId}/reviews`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get reviews:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve reviews',
      };
    }
  },

  // ========== DISPUTES ==========

  /**
   * File a dispute for a contract
   * @param {string} contractPublicId - Contract public ID
   * @param {string} reason - Dispute reason
   * @returns {Promise} Response with success message
   */
  fileDispute: async (contractPublicId, reason) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }
      if (!reason || reason.length < 10) {
        throw new Error('Dispute reason must be at least 10 characters');
      }

      const response = await api.post(
        `/contracts/${contractPublicId}/dispute`,
        { reason }
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to file dispute:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to file dispute',
      };
    }
  },

  /**
   * Resolve a dispute
   * @param {string} contractPublicId - Contract public ID
   * @param {string} resolution - Resolution description
   * @param {string} newStatus - New contract status
   * @returns {Promise} Response with success message
   */
  resolveDispute: async (contractPublicId, resolution, newStatus) => {
    try {
      if (!contractPublicId) {
        throw new Error('Contract public ID is required');
      }

      const payload = {
        resolution,
        new_status: newStatus,
      };

      const response = await api.post(
        `/contracts/${contractPublicId}/dispute/resolve`,
        payload
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to resolve dispute:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resolve dispute',
      };
    }
  },

  // ========== CATEGORIES & SKILLS ==========

  /**
   * Get available categories
   * @returns {Promise} Response with categories data
   */
  getCategories: async () => {
    try {
      const response = await api.get('/contracts/categories');
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get categories:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve categories',
      };
    }
  },

  /**
   * Get popular skills
   * @param {number} limit - Result limit (default: 20)
   * @returns {Promise} Response with skills data
   */
  getPopularSkills: async (limit = 20) => {
    try {
      const response = await api.get('/contracts/skills', {
        params: { limit },
      });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get skills:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve skills',
      };
    }
  },
};

/**
 * Utility functions for contracts
 */
export const contractsUtils = {
  /**
   * Format contract data for display
   * @param {Object} contract - Raw contract data from API
   * @returns {Object} Formatted contract data
   */
  formatContract: (contract) => {
    if (!contract) return null;

    return {
      ...contract,
      techStack: contract.tech_stack || [],
      createdAt: new Date(contract.created_at),
      updatedAt: new Date(contract.updated_at),
      startDate: contract.start_date ? new Date(contract.start_date) : null,
      endDate: contract.end_date ? new Date(contract.end_date) : null,
    };
  },

  /**
   * Calculate contract progress percentage
   * @param {Object} contract - Contract data with milestones
   * @returns {number} Progress percentage (0-100)
   */
  calculateProgress: (contract) => {
    if (!contract.milestones || contract.milestones.length === 0) return 0;

    const completed = contract.milestones.filter((m) => m.is_completed).length;
    return Math.round((completed / contract.milestones.length) * 100);
  },

  /**
   * Validate contract creation data
   * @param {Object} contractData - Contract data to validate
   * @returns {Object} Validation result with isValid (boolean) and errors (object mapping field names to error messages)
   */
  validateContractData,

  /**
   * Validate proposal data
   * @param {Object} proposalData - Proposal data to validate
   * @returns {Object} Validation result with isValid (boolean) and errors (object mapping field names to error messages)
   */
  validateProposalData: (proposalData) => {
    const errors = {};

    if (!proposalData.proposalText || proposalData.proposalText.length < 50) {
      errors.proposalText = 'Proposal text must be at least 50 characters';
    }

    if (
      !proposalData.proposedAmountUsd ||
      proposalData.proposedAmountUsd <= 0
    ) {
      errors.proposedAmountUsd = 'Proposed amount must be greater than 0';
    }

    if (
      !proposalData.proposedTimelineDays ||
      proposalData.proposedTimelineDays <= 0
    ) {
      errors.proposedTimelineDays = 'Proposed timeline must be at least 1 day';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  /**
   * Format currency amount
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (default: USD)
   * @returns {string} Formatted currency string
   */
  formatCurrency: (amount, currency = 'USD', showSymbol = true) => {
    return new Intl.NumberFormat('en-US', {
      style: showSymbol ? 'currency' : 'decimal',
      currency: showSymbol ? currency : undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  },

  /**
   * Get status badge color
   * @param {string} status - Contract status
   * @returns {string} Color class or hex code
   */
  getStatusColor: (status) => {
    const statusColors = {
      open: '#10b981', // green
      in_progress: '#3b82f6', // blue
      completed: '#8b5cf6', // purple
      cancelled: '#6b7280', // gray
      disputed: '#ef4444', // red
    };

    return statusColors[status] || '#6b7280';
  },

  /**
   * Calculate time remaining for contract
   * @param {string} endDate - Contract end date
   * @returns {Object} Time remaining object
   */
  calculateTimeRemaining: (endDate) => {
    if (!endDate) return null;

    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;

    if (diff <= 0) return { expired: true };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    return {
      expired: false,
      days,
      hours,
      text: days > 0 ? `${days} days` : `${hours} hours`,
    };
  },

  /**
   * Get expertise level display name
   * @param {string} level - Expertise level code
   * @returns {string} Display name
   */
  getExpertiseLevelName: (level) => {
    const levels = {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced',
      expert: 'Expert',
    };

    return levels[level] || level;
  },

  /**
   * Parse tech stack string
   * @param {string|Array} techStack - Tech stack as string or array
   * @returns {Array} Array of technologies
   */
  parseTechStack: (techStack) => {
    if (!techStack) return [];
    if (Array.isArray(techStack)) return techStack;
    if (typeof techStack === 'string') {
      return techStack
        .split(',')
        .map((tech) => tech.trim())
        .filter(Boolean);
    }
    return [];
  },

  /**
   * Check if contract is editable
   * @param {Object} contract - Contract object
   * @param {string} userRole - User role ('worker' or 'business')
   * @returns {boolean} Whether contract is editable
   */
  isContractEditable: (contract, userRole) => {
    if (!contract) return false;

    // Only open contracts can be edited
    if (contract.status !== 'open') return false;

    // Only business owner can edit
    // This would need additional user context in real implementation
    return userRole === 'business';
  },

  /**
   * Generate contract summary
   * @param {Object} contract - Contract object
   * @returns {string} Contract summary text
   */
  generateSummary: (contract) => {
    if (!contract) return '';

    const type =
      contract.payment_type === 'hourly'
        ? `Hourly (${contractsUtils.formatCurrency(contract.hourly_rate)}/hr)`
        : `Fixed (${contractsUtils.formatCurrency(contract.amount_usd)})`;

    const duration = contract.expected_duration_days
      ? `${contract.expected_duration_days} days`
      : 'Flexible';

    return `${type} • ${duration} • ${contractsUtils.getExpertiseLevelName(
      contract.expertise_level
    )}`;
  },

  /**
   * Get time ago string
   * @param {Date|string} timestamp - Timestamp to format
   * @returns {string} Time ago string
   */
  getTimeAgo: (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  },

  /**
   * Extract value from Go nullable type
   * @param {Object} nullable - Go nullable type with Valid and value properties
   * @returns {*} The actual value if valid, null otherwise
   */
  extractNullableValue: (nullable) => {
    if (!nullable || typeof nullable !== 'object') return nullable;
    if (nullable.Valid === false) return null;

    // Handle different Go nullable types
    if (nullable.Float64 !== undefined) return nullable.Float64;
    if (nullable.Int32 !== undefined) return nullable.Int32;
    if (nullable.Int64 !== undefined) return nullable.Int64;
    if (nullable.String !== undefined) return nullable.String;
    if (nullable.Time !== undefined) return nullable.Time;

    return nullable;
  },

  /**
   * Get milestone amount safely handling nullable values
   * @param {Object} milestone - Milestone object
   * @returns {number|null} Milestone amount or null
   */
  getMilestoneAmount: (milestone) => {
    if (!milestone) return null;

    // Handle direct amount (legacy format)
    if (typeof milestone.amount_usd === 'number') {
      return milestone.amount_usd;
    }
    if (typeof milestone.amountUsd === 'number') {
      return milestone.amountUsd;
    }

    // Handle nullable amount (new Go format)
    return contractsUtils.extractNullableValue(milestone.amount_usd);
  },
};

export default contractsService;
